import { type AuthUser, useAuthStore } from '@/lib/auth/store'
import { env } from '@/lib/env'
import { ApiError } from './errors'
import type { ApiErrorCode } from './errors'

interface ApiClientOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  // Si true, no se adjunta Authorization: Bearer (para login, register, etc.)
  skipAuth?: boolean
  // Si true, no se reintenta tras un 401 (evita bucle infinito en el refresh)
  skipRefreshOnUnauthorized?: boolean
}

// Mutex global para evitar múltiples refreshes simultáneos.
// Si varias peticiones fallan con 401 al mismo tiempo, solo una llama a /token/refresh.
let refreshInFlight: Promise<void> | null = null

export const apiClient = async <T>(path: string, options: ApiClientOptions = {}): Promise<T> => {
  const { body, skipAuth, skipRefreshOnUnauthorized, headers, ...rest } = options

  const accessToken = useAuthStore.getState().accessToken
  const finalHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Abakoo-Key': env.VITE_ABAKOO_API_KEY,
    ...(headers as Record<string, string> | undefined),
  }
  if (!skipAuth && accessToken) {
    finalHeaders.Authorization = `Bearer ${accessToken}`
  }

  // Construir fetchInit sin body cuando es undefined (exactOptionalPropertyTypes)
  const fetchInit: RequestInit = {
    ...rest,
    headers: finalHeaders,
    credentials: 'include', // Siempre — necesario para que la cookie abk_rt viaje
  }
  if (body !== undefined) {
    fetchInit.body = JSON.stringify(body)
  }

  const response = await fetch(`${env.VITE_API_URL}${path}`, fetchInit)

  // Interceptor 401: si el access token expiró, hace silent refresh y reintenta
  if (response.status === 401 && !skipRefreshOnUnauthorized && !skipAuth) {
    refreshInFlight ??= refreshAccessToken()
    try {
      await refreshInFlight
    } finally {
      refreshInFlight = null
    }
    return apiClient<T>(path, { ...options, skipRefreshOnUnauthorized: true })
  }

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as Record<string, unknown>
    throw new ApiError(
      response.status,
      (errorBody.errorCode as ApiErrorCode | undefined) ?? 'UNKNOWN',
      (errorBody.detail as string | undefined) ??
        (errorBody.title as string | undefined) ??
        response.statusText,
      errorBody.correlationId as string | undefined,
      errorBody,
    )
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

// Exportada para que guards.ts pueda usarla en el silent refresh del beforeLoad.
// Usa fetch directo (no apiClient) para evitar recursión.
export const refreshAccessToken = async (): Promise<void> => {
  const response = await fetch(`${env.VITE_API_URL}/api/v1/auth/token/refresh`, {
    method: 'POST',
    headers: { 'X-Abakoo-Key': env.VITE_ABAKOO_API_KEY },
    credentials: 'include',
  })

  if (!response.ok) {
    useAuthStore.getState().clear()
    throw new ApiError(response.status, 'REFRESH_FAILED', 'Refresh token inválido o expirado')
  }

  const data = (await response.json()) as {
    accessToken: string
    expiresAt: string
    user: AuthUser
  }
  useAuthStore.getState().setAuth(data.accessToken, data.expiresAt, data.user)
}
